import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { StarRating } from "../ratingComponents";
import styles from "./Review.scss";
import classnamesBind from "classnames/bind";
import ExpandableText from "../expandableText/ExpandableText";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { getFeature } from "../../utils/features";
import ReportReviewButton from "../reportReviewButton/ReportReviewButton";
import {
  SYNDICATED_REVIEW,
  STAFF_REVIEW,
  VERIFIED_REVIEW,
  INCENTIVIZED_REVIEW,
} from "../../constants";
import IncentivisedBadge from "../incentivisedBadge/IncentivisedBadge";

const classnames = classnamesBind.bind(styles);

const Review = ({
  title,
  reviewText,
  rating,
  submissionRecency,
  syndicationSource,
  badgesOrder,
  photos,
  additionalClasses,
  displayPhotos,
  togglePhotoGallery,
  onExpandCallback,
  reviewIndex = 1,
  id,
  badges,
  onReportSuccess,
  isReported,
  reviewIsInModal,
}) => {
  const { getTranslation } = useRuntimeConstants();
  const reviewSource = getReviewSource(syndicationSource, badgesOrder, badges);
  const isIncentivisedBadgeEnabled = getFeature(
    "dmcca-incentivised-badge"
  ).isEnabled();
  const isReportReviewsEnabled = getFeature("dmcca-report-reviews").isEnabled();
  const [showIncentivisedBadge, setShowIncentivisedBadge] = useState(false);
  const dmccaFeatures = () => {
    return (
      <>
        <div className={styles.reviewBottomSectionContainer}>
          {showIncentivisedBadge && <IncentivisedBadge />}
          {isReportReviewsEnabled && (
            <ReportReviewButton
              reviewId={id}
              onReportSuccess={onReportSuccess}
              isReported={isReported}
              reviewIsInModal={reviewIsInModal}
            />
          )}
        </div>
      </>
    );
  };

  useEffect(() => {
    const shouldShowBadge =
      isIncentivisedBadgeEnabled && badges?.incentivizedReview;
    setShowIncentivisedBadge(shouldShowBadge);

    if (shouldShowBadge) {
      requestAnimationFrame(() => {
        onExpandCallback?.();
      });
    }
  }, [
    badges?.incentivizedReview,
    isIncentivisedBadgeEnabled,
    onExpandCallback,
  ]);

  const ratingsAriaLabel = getRatingAriaLabel(
    getTranslation,
    {
      submissionRecency,
      syndicationSource,
      rating,
      reviewSource,
    },
    reviewIndex
  );
  const ratingsText = getSourceText(getTranslation, {
    syndicationSource,
    rating,
    reviewSource,
  });

  return (
    <section
      className={additionalClasses}
      aria-label={ratingsAriaLabel}
      role={"group"}
    >
      <div
        className={styles.rating}
        tabIndex={-1}
        data-testid="reviewsModalReview"
      >
        <div className={styles.starsAndRecencyContainer}>
          <StarRating rating={rating} />
          <span className={styles.recency}>{submissionRecency}</span>
        </div>
        <p className={styles.source}>{ratingsText}</p>
      </div>
      {title && (
        <h4
          className={classnames(styles.title, styles.headerSpace, {
            contentSpace: !!reviewText,
          })}
        >
          {title}
        </h4>
      )}
      {reviewText && (
        <>
          <ExpandableText
            hasTitle={!!title}
            text={reviewText}
            readMoreTranslationKey="rdp_ratings_reviews_read_more"
            testId="reviewsReadMore"
            onExpandCallback={onExpandCallback}
            reviewIsInModal={reviewIsInModal}
          />
        </>
      )}
      {dmccaFeatures()}
      {shouldDisplayPhotos({ photos, displayPhotos, togglePhotoGallery }) && (
        <div className={styles.thumbnails}>
          {photos.map(({ thumbnailUrl }, index) => (
            <button
              data-testid="reviewsModalThumbnailButton"
              className={styles.thumbnail}
              key={thumbnailUrl}
              aria-label={getTranslation(
                "pdp_media_gallery_thumbnail_instruction"
              )}
              onClick={(event) =>
                togglePhotoGallery(true, photos, index, event.target)
              }
            >
              <img
                className={styles.thumbnailImage}
                src={thumbnailUrl}
                loading="lazy"
                alt={getTranslation(
                  "rdp_ratings_reviews_accessibility_image_description"
                )}
              />
            </button>
          ))}
        </div>
      )}
    </section>
  );
};

Review.propTypes = {
  id: PropTypes.number.isRequired,
  title: PropTypes.string,
  reviewText: PropTypes.string,
  rating: PropTypes.number.isRequired,
  isReported: PropTypes.bool.isRequired,
  submissionRecency: PropTypes.string.isRequired,
  syndicationSource: PropTypes.shape({ name: PropTypes.string.isRequired }),
  badges: PropTypes.shape({
    incentivizedReview: PropTypes.shape({
      contentType: PropTypes.string,
      id: PropTypes.string,
      badgeType: PropTypes.string,
    }),
  }),
  badgesOrder: PropTypes.array.isRequired,
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      thumbnailUrl: PropTypes.string.isRequired,
    })
  ).isRequired,
  additionalClasses: PropTypes.string,
  displayPhotos: PropTypes.bool,
  togglePhotoGallery: PropTypes.func,
  onExpandCallback: PropTypes.func,
  onReportSuccess: PropTypes.func,
  reviewIsInModal: PropTypes.bool,
};

const shouldDisplayPhotos = ({ photos, displayPhotos, togglePhotoGallery }) =>
  !!(displayPhotos && togglePhotoGallery && photos.length);

const getReviewSource = (syndicationSource, badgesOrder, badges) => {
  if (syndicationSource) {
    return SYNDICATED_REVIEW;
  }
  if (badgesOrder.length) {
    if (badgesOrder.includes("Staff")) {
      return STAFF_REVIEW;
    }
    return VERIFIED_REVIEW;
  }
  if (badges?.incentivizedReview) {
    return INCENTIVIZED_REVIEW;
  }
};

const getSourceText = (
  translation,
  { syndicationSource = null, reviewSource = null }
) => {
  return {
    [SYNDICATED_REVIEW]: translation(
      "pdp_ratings_reviews_syndication_badge_text",
      {
        syndicationSourceName: syndicationSource?.name,
      }
    ),
    [STAFF_REVIEW]: translation(
      "pdp_ratings_reviews_verified_staff_purchase_badge_text"
    ),
    [VERIFIED_REVIEW]: translation(
      "pdp_ratings_reviews_verified_purchaser_badge_text"
    ),
  }[reviewSource];
};

const getRatingAriaLabel = (
  translation,
  { submissionRecency, syndicationSource, rating, reviewSource },
  reviewIndex
) => {
  if (reviewSource === SYNDICATED_REVIEW) {
    return `${translation("pdp_ratings_reviews_accessibility_identifier", {
      smart_count: reviewIndex,
      count: reviewIndex,
    })}. ${translation(
      "pdp_ratings_reviews_accessibility_individual_review_intro_syndicated",
      {
        submissionRecency,
        syndicationSourceName: syndicationSource?.name ?? "",
        rating,
      }
    )}`;
  }

  return `${translation("pdp_ratings_reviews_accessibility_identifier", {
    smart_count: reviewIndex,
    count: reviewIndex,
  })}. ${translation(
    "pdp_ratings_reviews_accessibility_individual_review_intro_verified_purchaser",
    {
      submissionRecency,
      badgeName: getSourceText(translation, {
        syndicationSource,
        reviewSource,
      }),
      rating,
    }
  )}`;
};

export default Review;

Review.propTypes = {
  reviewIndex: PropTypes.number,
};
